// FaFaCat 遊戲常數 — 對應 Unity Slot001_Constants.cs / ReelSettingSO.cs。
// 全部以唯讀（as const）定義，避免來源版本「可變 static List」的全域狀態問題。

/** 來源版本字串（Unity Slot001_Constants.Version） */
export const SOURCE_VERSION = 'V 0.11.5 2025081900' as const

// ── 盤面尺寸（FaFaCatReelSetting 資產實值：3×3） ─────────────────────
// 註：ReelSettingSO.cs 預設為 6×6，但實際 .asset 覆寫為 ReelCount:3, ColCount:3。
export const REEL_COUNT = 3 as const // 顯示幾輪
export const ROW_COUNT = 3 as const // 顯示幾排（Unity ColCount）

// ── 符號 ────────────────────────────────────────────────────────────
/** 符號名稱，索引即符號 ID（0=Wild） */
export const SYMBOL_NAMES = ['WW', 'H1', 'H2', 'H3', 'LA', 'LK', 'LQ', 'LJ'] as const
export const SYMBOL_WILD_INDEX = 0 as const
export const SYMBOL_WHEEL_INDEX = 0 as const
/** 連線判定門檻：相鄰相符數 ≥ 2（即由左連續 3 個以上）。對應 Unity CheckWin 的 `lineCount >= 2`。 */
export const MIN_LINE_MATCH = 2 as const

// ── 倍數系統 ────────────────────────────────────────────────────────
/** 統一倍數陣列：索引 0=Wheel, 1=X1, 2=X2, 3=X3, 4=X5, 5=X10, 6=X15 */
export const ALL_MULTIPLIER_ARRAY = [0, 1, 2, 3, 5, 10, 15] as const
/**
 * 倍數轉輪的 Wheel 符號值（= 0；ALL_MULTIPLIER_ARRAY[0]）。
 * 唯有倍數轉輪「停在此符號」才會進入 Bonus 轉盤演出，
 * 對應 Unity Slot001_MultiplierReels.CheckWin 的 `MGMultiplier == 0`。
 */
export const MULTIPLIER_WHEEL_VALUE = 0 as const
/** 一般模式可用倍數索引（含 X1、不含 X15） */
export const NORMAL_AVAILABLE_INDEXES = [0, 1, 2, 3, 4, 5] as const
/** ExtraBet 模式可用倍數索引（不含 X1、含 X15） */
export const EXTRA_BET_AVAILABLE_INDEXES = [0, 2, 3, 4, 5, 6] as const
/** ExtraBet 加注倍率 */
export const EXTRA_BET_MULTIPLE = 1.5 as const

// ── Bonus 轉盤 ──────────────────────────────────────────────────────
export const WHEEL_INNER_MULTIPLIER_ARRAY = [3, 200, 1000, 1, 20, 100, 5, 10] as const
export const WHEEL_OUTER_MULTIPLIER_ARRAY = [1, 2, 3, 5, 10, 15] as const

// ── 購買類型 ────────────────────────────────────────────────────────
export const BuyType = {
  Normal: 0,
  ExtraBet: 1,
  BuyFreeSpins: 2,
  BuySuperFreeSpins: 3,
} as const
export type BuyTypeValue = (typeof BuyType)[keyof typeof BuyType]

/**
 * 依 ExtraBet 狀態取得可用倍數索引。對應 Slot001_Constants.GetAvailableMultiplierIndexes。
 */
export function getAvailableMultiplierIndexes(isExtraBet: boolean): readonly number[] {
  return isExtraBet ? EXTRA_BET_AVAILABLE_INDEXES : NORMAL_AVAILABLE_INDEXES
}

/**
 * 依 ExtraBet 狀態取得倍數轉輪（第四輪）可顯示的「倍數幣值」（排除 Wheel=0）。
 * 一般模式 → [1,2,3,5,10]（含 X1、不含 X15）；ExtraBet → [2,3,5,10,15]（不含 X1、含 X15）。
 * 對應 Unity Slot001_MultiplierReel.GetAvailableMultipliers 過濾後的可建立符號集合。
 */
export function getAvailableMultiplierValues(isExtraBet: boolean): readonly number[] {
  const values: number[] = []
  for (const index of getAvailableMultiplierIndexes(isExtraBet)) {
    const value = ALL_MULTIPLIER_ARRAY[index]
    if (value !== undefined && value !== MULTIPLIER_WHEEL_VALUE) values.push(value)
  }
  // 不變式：兩種模式都至少有 5 個倍數幣值。為空代表索引常數被改錯，應立即失敗而非靜默退回 X1。
  if (values.length === 0) {
    throw new Error(`倍數轉輪可用值為空（isExtraBet=${isExtraBet}）— 請檢查倍數索引常數`)
  }
  return values
}

/** 由倍數值取得在 ALL_MULTIPLIER_ARRAY 的索引，找不到回 -1。 */
export function getMultiplierIndex(multiplier: number): number {
  return ALL_MULTIPLIER_ARRAY.indexOf(multiplier as (typeof ALL_MULTIPLIER_ARRAY)[number])
}
